import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, HelpCircle, Lock, Shield, Unlock, Clock, CreditCard, Star, AlertTriangle } from 'lucide-react';

const FAQ_DATA = [
  {
    id: "what-are-locked-funds",
    question: "What are locked funds?",
    answer: "Locked funds are a portion of your money temporarily held as part of AegisPay\u2019s security system to help ensure all members complete their contributions. These funds are not lost and will be released at the appropriate stage of the savings cycle."
  },
  {
    id: "why-extra-locked",
    question: "Why was extra money locked from my payout?",
    answer: "A small portion of funds may be held during payout as part of the group\u2019s protection system. This helps maintain stability and ensures all members receive their payouts as expected."
  },
  {
    id: "how-sph-calculated",
    question: "How is the security protection hold calculated?",
    answer: "The security hold is automatically determined by the system based on the structure of your savings group. This calculation is designed to fairly protect all members and ensure smooth payout cycles."
  },
  {
    id: "is-penalty",
    question: "Is the security hold a penalty?",
    answer: "No. The security hold is not a penalty. It is a protective measure used to safeguard all members in the group and ensure commitments are fulfilled."
  },
  {
    id: "when-released",
    question: "When will the security hold be released?",
    answer: "Locked funds are released at specific stages of the savings cycle, typically after key contribution milestones are completed. You will be notified once your funds are available."
  },
  {
    id: "lock-qualification",
    question: "What is the 50% lock qualification deposit?",
    answer: "This is a required commitment deposit that helps confirm your participation in the group. It also serves as a protective layer to ensure all members are committed to completing the savings cycle."
  },
  {
    id: "withdraw-early",
    question: "Can I withdraw locked funds early?",
    answer: "Locked funds are part of the group\u2019s protection system and are generally not available for early withdrawal. However, in special cases, a request may be reviewed based on group rules."
  },
  {
    id: "money-safe",
    question: "Is my money safe?",
    answer: "Yes. AegisPay uses structured systems and protection rules designed to safeguard user funds and ensure fair participation for all members."
  },
  {
    id: "track-locked",
    question: "How can I track my locked funds?",
    answer: "You can view your locked funds in your wallet under the \u201cLocked Balance\u201d section, where details and release timelines are displayed."
  },
  {
    id: "auto-debit",
    question: "What is Auto-debit?",
    answer: "Auto-debit is a feature that automatically deducts your contribution from your wallet based on your group schedule, helping you stay consistent and avoid missed payments."
  },
  {
    id: "insufficient-funds",
    question: "What happens if I don't have enough funds for Auto-debit?",
    answer: "If there are insufficient funds, the system will notify you. You may be given a short window to fund your wallet before further actions are taken according to group rules."
  },
  {
    id: "credibility-score",
    question: "What is the credibility score?",
    answer: "Your credibility score reflects your reliability within the platform, based on your contribution history, consistency, and participation in savings groups. A higher score increases your trust level within the community."
  },
  {
    id: "penalty-rate",
    question: "What is the penalty for missed payments?",
    answer: "Missed payments may affect your credibility score and may trigger certain protective measures within the group. These measures are designed to maintain fairness and protect other members."
  }
];

const Help = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="help-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white shadow-sm hover:shadow-md transition-shadow"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#0B1F3A]">Help & FAQs</h1>
            <p className="text-sm text-slate-500">Everything you need to know</p>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 bg-gradient-to-r from-[#0B1F3A] to-[#1a3a8f] text-white">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-white/20">
                <Shield size={20} />
              </div>
              <div>
                <p className="font-semibold text-white">Your funds are protected</p>
                <p className="text-sm text-white mt-1">
                  Locked funds are automatically released once group conditions are met. No money is lost.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lock Types Overview */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="p-2 rounded-xl bg-[#0B1F3A]/10 w-fit mb-3">
                <Shield size={18} className="text-[#0B1F3A]" />
              </div>
              <p className="font-medium text-[#0B1F3A] text-sm">Lock Qualification</p>
              <p className="text-xs text-slate-500 mt-1">Commitment deposit to join groups</p>
              <div className="mt-2 flex items-center gap-1 text-xs text-[#2272C5]">
                <Unlock size={12} />
                <span>Released at group completion</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="p-2 rounded-xl bg-amber-100 w-fit mb-3">
                <Lock size={18} className="text-amber-600" />
              </div>
              <p className="font-medium text-[#0B1F3A] text-sm">Security Hold</p>
              <p className="text-xs text-slate-500 mt-1">Automatic protection for members</p>
              <div className="mt-2 flex items-center gap-1 text-xs text-[#2272C5]">
                <Unlock size={12} />
                <span>Released at key milestones</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Accordion */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle size={18} className="text-[#2272C5]" />
              <h2 className="font-semibold text-[#0B1F3A]">Frequently Asked Questions</h2>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              {FAQ_DATA.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id}>
                  <AccordionTrigger className="text-left text-[#0B1F3A] text-sm hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-sm">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Contact Support */}
        <div className="mt-6 p-4 rounded-2xl bg-slate-100 text-center">
          <p className="text-sm text-slate-600">
            Still have questions? Contact our support team for assistance.
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Help;
